// CloseAsDuplicateModal — confirm closing a duplicate_of child ticket (canonical unchanged).

import React from 'react';
import { createPortal } from 'react-dom';

/** Above shared Modal stack (112–125) when opened from Ticket Information. */
const NESTED_MODAL_Z = 130;

export const getTicketStatusCode = (ticket) => {
  if (ticket?.statusCode != null && typeof ticket.statusCode === 'string') {
    return String(ticket.statusCode).toLowerCase().replace(/[\s-]+/g, '_');
  }
  if (ticket?.status != null && typeof ticket.status === 'object') {
    const nested =
      ticket.status.statusCode
      || ticket.status.code
      || ticket.status.name
      || '';
    return String(nested).toLowerCase().replace(/[\s-]+/g, '_');
  }
  if (typeof ticket?.status === 'string') {
    return String(ticket.status).toLowerCase().replace(/[\s-]+/g, '_');
  }
  return '';
};

/** Eligibility for Close as Duplicate UI (backend remains authoritative). */
export const canShowCloseAsDuplicateAction = ({
  ticketRelationsEnabled,
  duplicateOf,
  ticket,
  allowInteraction = true,
  hasAgentSession = Boolean(
    typeof sessionStorage !== 'undefined' && sessionStorage.getItem('roleId')
  ),
}) => {
  if (!ticketRelationsEnabled || !duplicateOf || !allowInteraction || !hasAgentSession) {
    return false;
  }
  const status = getTicketStatusCode(ticket);
  return status !== 'closed' && status !== 'resolved';
};

const CloseAsDuplicateModal = ({
  isOpen,
  onClose,
  currentTicketNumber,
  canonicalTicketNumber,
  reason,
  onReasonChange,
  error,
  submitting,
  onSubmit,
}) => {
  if (!isOpen) return null;

  const currentLabel = currentTicketNumber || 'this ticket';
  const canonicalLabel = canonicalTicketNumber || 'the original ticket';

  const modalTree = (
    <div
      className="fixed inset-0 flex items-center justify-center bg-black/50 px-4"
      style={{ zIndex: NESTED_MODAL_Z }}
    >
      <div
        className="relative w-full max-w-lg rounded-lg bg-white dark:bg-gray-800 p-6 shadow-xl"
        style={{ zIndex: NESTED_MODAL_Z + 1 }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="close-as-duplicate-title"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 id="close-as-duplicate-title" className="text-lg font-semibold text-gray-900 dark:text-gray-100">
          Close as Duplicate
        </h3>
        <p className="mt-3 text-sm text-gray-700 dark:text-gray-300">
          This ticket ({currentLabel}) will be closed as a duplicate of{' '}
          <span className="font-medium font-mono">{canonicalLabel}</span>.
        </p>
        <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
          The original ticket will remain open and unchanged.
        </p>
        <label htmlFor="close-as-duplicate-reason" className="block mt-4 text-sm font-medium text-gray-700 dark:text-gray-300">
          Reason <span className="text-red-500">*</span>
        </label>
        <textarea
          id="close-as-duplicate-reason"
          rows={4}
          value={reason}
          onChange={(e) => onReasonChange(e.target.value)}
          className="mt-1 w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
          placeholder="Enter the reason for closing this ticket as a duplicate..."
          disabled={submitting}
        />
        {error && (
          <p className="mt-2 text-sm text-red-600 dark:text-red-400">{error}</p>
        )}
        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2 text-sm font-medium rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onSubmit}
            disabled={submitting}
            className="px-4 py-2 text-sm font-medium rounded-md bg-gray-800 hover:bg-gray-900 text-white disabled:opacity-50"
          >
            {submitting ? 'Closing…' : 'Close as Duplicate'}
          </button>
        </div>
      </div>
    </div>
  );

  if (typeof document === 'undefined') return null;
  return createPortal(modalTree, document.body);
};

export default CloseAsDuplicateModal;
